import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { whoami, pkceInit, getRedirectUri } from "services/omuService";
import { setAuth } from "redux/actions/authAction";
import LoadingProgress from "components/loading-progress/loading-progress";
import { OAUTH_CALLBACK_PATH, PREV_PATH_KEY } from "constants/auth";

const OMU_SKIP_PATHS = ["/omu/login", "/omu/register"];

const AuthGuard = ({ children }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const username = useSelector((state) => state.authReducer.username);

  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      const pathname = location.pathname.replace(/\/$/, "") || "/";

      const isOmuPath = pathname.startsWith("/omu");
      const isSkipped =
        !isOmuPath ||
        OMU_SKIP_PATHS.includes(pathname) ||
        pathname === OAUTH_CALLBACK_PATH ||
        pathname === "/access-denied";

      if (isSkipped) {
        if (mounted) setReady(true);
        return;
      }

      if (username) {
        if (mounted) setReady(true);
        return;
      }

      try {
        const res = await whoami();
        if (!mounted) return;
        const { username: user, groups, permissions } = res.data.response;
        dispatch(setAuth(user, groups, permissions));
        setReady(true);
      } catch (err) {
        if (!mounted) return;
        const status = err.response?.status;

        if (status === 401) {
          sessionStorage.setItem(
            PREV_PATH_KEY,
            location.pathname + location.search,
          );
          try {
            const res = await pkceInit(getRedirectUri());
            if (!mounted) return;
            window.location.href = res.data.authorize_url;
          } catch {
            if (!mounted) return;
            history.replace("/access-denied");
            setReady(true);
          }
        } else if (status === 403) {
          history.replace("/access-denied");
          setReady(true);
        } else {
          history.replace("/access-denied");
          setReady(true);
        }
      }
    };

    checkAuth();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) {
    return <LoadingProgress />;
  }

  return children;
};

export default AuthGuard;
